import {Dialog} from '@ui/overlays/dialog/dialog';
import {DialogHeader} from '@ui/overlays/dialog/dialog-header';
import {Trans} from '@ui/i18n/trans';
import {DialogBody} from '@ui/overlays/dialog/dialog-body';
import {useDialogContext} from '@ui/overlays/dialog/dialog-context';
import {DialogFooter} from '@ui/overlays/dialog/dialog-footer';
import {Button} from '@ui/buttons/button';
import {Skeleton} from '@ui/skeleton/skeleton';
import React, {Fragment, useState} from 'react';
import {useAuth} from '@common/auth/use-auth';
import TurnstileWidget from '@app/liens/turnstile-widget';
import {Link} from 'react-router-dom';
import {Nzb} from '@app/titles/models/nzb';
import {DescriptionIcon} from '@ui/icons/material/Description';
import {Accordion, AccordionItem} from '@ui/accordion/accordion';
import {FormattedBytes} from '@ui/i18n/formatted-bytes';
import {Tooltip} from '@ui/tooltip/tooltip';
import {FormattedDate} from '@ui/i18n/formatted-date';
import {UploadIcon} from '@ui/icons/material/Upload';
import {ResponseDownload, useDownloadNzb} from '@app/liens/use-download-nzb';
import {SectionHelper} from '@common/ui/other/section-helper';
import {ErrorIcon} from '@ui/icons/material/Error';
import {
  setInLocalStorage,
  useLocalStorage,
} from '@ui/utils/hooks/local-storage';

interface NzbIdDialogProps {
  id: number;
}

export function DownloadNzbDialog({id}: NzbIdDialogProps) {
  const {close} = useDialogContext();
  const {user} = useAuth();
  const [token, setToken] = useState('');

  const handleCaptchaVerify = (token: string) => {
    setToken(token);
  };

  const importItem = useDownloadNzb(
    id,
    !!(user?.IsPremium || token?.length),
    undefined,
    token,
  );

  return (
    <Dialog size={'2xl'}>
      <DialogHeader>
        <Trans message={user?.IsPremium ? 'Premium Download' : 'Download'} />
      </DialogHeader>
      <DialogBody>
        {!user?.IsPremium && !token ? (
          <TurnstileWidget onVerify={handleCaptchaVerify} />
        ) : (
          <div className="overflow-hidden overflow-ellipsis">
            {importItem.data ? download({data: importItem.data}) : <Skeleton />}
          </div>
        )}
      </DialogBody>
      <DialogFooter>
        <Button onClick={() => close()}>
          <Trans message="Close" />
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

function download({data}: {data: ResponseDownload}) {
  const ButtonDownload = ({
    nzb,
    download_url,
  }: {
    nzb: Nzb;
    download_url: string;
  }) => (
    <div className="mt-24 flex h-full flex-auto  items-center gap-18 overflow-x-scroll whitespace-nowrap rounded-panel border p-20">
      <div className="flex-shrink-0 rounded-lg bg-primary-light/20 p-10 text-primary">
        <Link to={download_url} target={'_blank'}>
          <svg
            aria-hidden="true"
            focusable="false"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            data-testid="Icon"
            className="svg-icon icon-lg"
            height="1em"
            width="1em"
          >
            <path d="M19 9h-4V3H9v6H5l7 7 7-7zm-8 2V5h2v6h1.17L12 13.17 9.83 11H11zm-6 7h14v2H5z"></path>
          </svg>
        </Link>
      </div>
      <div className="flex-auto">
        <div className="flex items-center justify-between gap-20">
          <div className="font-bold text-main">
            <div className={'inline-flex'}>
              <Trans message={'Groups'} />
              <span className={'mx-5'}>
                {nzb.groups?.length &&
                  nzb.groups?.map((group, index) => (
                    <span key={index} className=" text-sm text-muted">
                      {group}
                      {index < nzb.groups.length - 1 ? ', ' : ''}
                    </span>
                  ))}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between gap-20">
          <div className="font-bold text-main">
            <Trans message={'By'} />
            <span className={'mx-5'}>
              <span className="text-sm text-muted">{nzb.poster}</span>
            </span>
          </div>
          <div className="flex items-center gap-10">
            <Tooltip label={<Trans message={'Posted At'} />}>
              <div className="text-sm font-semibold text-muted">
                <UploadIcon size={'sm'} className={'mb-3 mr-3'} />
                <FormattedDate preset={'short'} date={nzb.posted_at} />
              </div>
            </Tooltip>
          </div>
        </div>
        <div className="flex items-center justify-between gap-20">
          {nzb.meta?.length &&
            nzb.meta?.map((meta, index) => (
              <div key={index} className="font-bold text-main">
                <div className={'inline-flex'}>
                  <span className="text-sm text-muted">{meta.type}</span>
                  <span className="text-sm text-muted">: {meta.value}</span>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
  return (
    <Fragment>
      <SetupAlertsList />
      <ButtonDownload nzb={data.model} download_url={data.download_url} />
      <Accordion className={'mt-10'} variant={'outline'}>
        <AccordionItem
          label={
            <Fragment>
              <Trans
                message=":name (:countFiles Files)"
                values={{
                  countFiles: data.model.files.length,
                  name: data.model.name,
                }}
              />
              {' - '}
              <FormattedBytes bytes={data.model.size} />
            </Fragment>
          }
          startIcon={<DescriptionIcon />}
        >
          <div className="flex flex-col gap-10">
            {data.model.files.map((file, index) => (
              <div key={index} className="flex items-center gap-10">
                <div className="text-sm text-muted">{file}</div>
              </div>
            ))}
          </div>
        </AccordionItem>
      </Accordion>
    </Fragment>
  );
}

function SetupAlertsList() {
  const [dismissValue] = useLocalStorage<{
    timestamp: number;
  } | null>('ng-ads-alert-dismissed', null);

  // show alert if 1 day passed since last dismiss
  const shouldShowAlert =
    !dismissValue || Date.now() - dismissValue.timestamp > 86400000 * 3;

  if (!shouldShowAlert) {
    return null;
  }

  return (
    <div className="fixed left-24 right-24 top-24 z-10 mx-auto w-max max-w-[calc(100%-48px)] overflow-hidden rounded-panel bg shadow-md">
      <SetupAlert />
    </div>
  );
}

function SetupAlert() {
  return (
    <SectionHelper
      leadingIcon={<ErrorIcon size="xs" className="text-positive" />}
      onClose={() => {
        setInLocalStorage('ng-ads-alert-dismissed', {
          timestamp: Date.now(),
        });
      }}
      title="Usenet Partner"
      description={
        <div>
          <Trans
            message={'For better download speed, we recommend using our '}
          />
          <Link to={'/ng-pro'} className="text-primary" target={'_blank'}>
            <Trans message={'Usenet Partner'} />
          </Link>
        </div>
      }
      color="neutral"
    />
  );
}
